import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, HardHat, ShieldCheck, Globe } from "lucide-react";
import { notifyPing } from "@/lib/notify";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";

export default function ClientChat() {
  const { sid } = useParams();
  const { lang, toggle } = useLang();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [notFound, setNotFound] = useState(false);
  const endRef = useRef(null);
  const prevBiz = useRef(null);

  const load = useCallback(() => {
    api.get(`/chat/${sid}/messages`).then((r) => {
      const messages = r.data.messages || [];
      setMsgs(messages);
      const bizCount = messages.filter((m) => m.sender === "business").length;
      if (prevBiz.current !== null && bizCount > prevBiz.current) { notifyPing(); toast.info(lang === "es" ? "Nuevo mensaje" : "New message"); }
      prevBiz.current = bizCount;
      api.post(`/chat/${sid}/client-read`).catch(() => {});
    }).catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [sid, lang]);

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    const t2 = text; setText("");
    await api.post(`/chat/${sid}/client-message`, { text: t2 }).catch(() => {});
    load();
  };

  if (notFound) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">{lang === "es" ? "Esta conversación ya no está disponible." : "This conversation is no longer available."}</div>;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="flex items-center justify-between gap-2 bg-secondary px-5 py-4 text-white">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary"><HardHat className="h-4 w-4" /></span>
          <div><p className="font-head font-bold leading-tight">{lang === "es" ? "Chat con el profesional" : "Chat with the professional"}</p><p className="text-xs text-white/60">{lang === "es" ? "URBIS · conversación segura" : "URBIS · secure conversation"}</p></div>
        </div>
        <button onClick={toggle} className="flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 text-xs font-semibold text-white/80 hover:text-white" data-testid="client-chat-lang-toggle">
          <Globe className="h-3.5 w-3.5" /> {lang === "es" ? "EN" : "ES"}
        </button>
      </div>
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-6" data-testid="client-chat-page">
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-accent/30 bg-accent/5 px-3 py-2 text-xs text-emerald-800">
          <ShieldCheck className="h-4 w-4 text-accent" /> {lang === "es" ? "Por tu seguridad, la conversación ocurre dentro de la plataforma." : "For your safety, the conversation stays within the platform."}
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto rounded-xl bg-white p-4 shadow-sm">
          {msgs.length === 0 && <p className="py-10 text-center text-sm text-slate-400">{lang === "es" ? "Escribe tu primer mensaje…" : "Write your first message…"}</p>}
          {msgs.map((m, i) => (
            <div key={m.id || i} className={`w-fit max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === "client" ? "ml-auto bg-primary text-white" : "bg-slate-100 text-slate-800"}`}>{m.text}</div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="mt-3 flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={lang === "es" ? "Escribe un mensaje…" : "Type a message…"} className="rounded-full" data-testid="client-chat-input" />
          <Button onClick={send} className="rounded-full bg-primary px-4 hover:bg-orange-600" data-testid="client-chat-send"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
